import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, UserPlus } from "lucide-react";

/**
 * LiveSeatQueue — host-side pending-request panel. Renders only when
 * there's at least 1 pending seat request. Fetches from
 * `/api/live/{room}/seat-requests` on mount, refreshes on every
 * `refreshTrigger` bump (parent nudges it whenever the WS
 * `live:seat_request` event fires), and polls every 20s as a
 * safety-net.
 *
 * Each row shows the requester's avatar + name + how long ago they
 * asked, with tap-to-approve (green check) and tap-to-deny (red X)
 * buttons. Approve → viewer becomes a co-host. Deny → silently pulls
 * the request from the DB (no rejection notification, keeps the room
 * from becoming a rejection show).
 */

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

export default function LiveSeatQueue({ roomName, refreshTrigger = 0, seatMode }) {
  const [requests, setRequests] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [now, setNow] = useState(Date.now());

  const pull = useCallback(async () => {
    if (!roomName) return;
    try {
      const { data } = await api.get(`/live/${roomName}/seat-requests`);
      setRequests(data.requests || []);
    } catch { /* silent; likely 403 if not the host */ }
  }, [roomName]);

  useEffect(() => {
    pull();
    const id = setInterval(pull, 20000);
    // Ticker to keep the "s/m/h ago" label fresh
    const tick = setInterval(() => setNow(Date.now()), 15000);
    return () => { clearInterval(id); clearInterval(tick); };
  }, [pull]);

  useEffect(() => { if (refreshTrigger > 0) pull(); }, [refreshTrigger, pull]);

  async function approve(userId) {
    setBusyId(userId);
    try {
      await api.post(`/live/${roomName}/battle/approve-request`, { invitee_user_id: userId });
      setRequests((r) => r.filter((x) => x.user_id !== userId));
      toast.success("Guest approved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  }

  async function deny(userId) {
    setBusyId(userId);
    try {
      await api.post(`/live/${roomName}/battle/deny-request`, { invitee_user_id: userId });
      setRequests((r) => r.filter((x) => x.user_id !== userId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deny failed");
    } finally { setBusyId(null); }
  }

  if (!seatMode || requests.length === 0) return null;

  return (
    <div
      className="absolute z-20 top-16 right-3 md:top-auto md:bottom-32 w-64 max-w-[80vw] bg-black/85 backdrop-blur-md border border-[#39FF14]/50 shadow-[0_0_30px_rgba(57,255,20,0.25)] pointer-events-auto"
      data-testid="live-seat-queue"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <UserPlus className="w-3.5 h-3.5 text-[#39FF14]" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#39FF14]">
          seat requests · {requests.length}
        </span>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-white/5">
        {requests.map((r) => (
          <div
            key={r.user_id}
            className="flex items-center gap-2 px-3 py-2"
            data-testid={`live-seat-queue-row-${r.user_id}`}
          >
            {r.picture ? (
              <img src={r.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[11px] shrink-0">
                {(r.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">{r.name || "Unknown"}</p>
              <p className="text-[10px] font-mono text-white/50">{timeAgo(r.requested_at)} ago</p>
            </div>
            <button
              onClick={() => deny(r.user_id)}
              disabled={busyId === r.user_id}
              data-testid={`live-seat-deny-${r.user_id}`}
              className="p-1.5 bg-white/5 hover:bg-[#FF3B3B] text-white/70 hover:text-white transition-colors disabled:opacity-50"
              aria-label={`Deny ${r.name}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => approve(r.user_id)}
              disabled={busyId === r.user_id}
              data-testid={`live-seat-approve-${r.user_id}`}
              className="p-1.5 bg-[#39FF14] hover:bg-[#5EFF3D] text-black transition-colors disabled:opacity-50"
              aria-label={`Approve ${r.name}`}
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      {/* Unused `now` state exists to force periodic re-render for the
          "Xs ago" label. Referenced here so linters don't strip it. */}
      <span className="hidden" data-now={now} />
    </div>
  );
}
